import React, { useEffect, useState } from 'react';
import { MessageSquareQuote, Send, Star } from 'lucide-react';
import { api, formatApiError } from '../lib/api';

const STATUS_LABEL = {
    pending: 'În așteptare',
    approved: 'Publicat',
    rejected: 'Respins',
};

export function RestaurantTestimonialSettings() {
    const [eligibility, setEligibility] = useState(null);
    const [quote, setQuote] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const load = () => {
        setLoading(true);
        api.get('/r/testimonials/eligibility')
            .then((r) => setEligibility(r.data))
            .catch(() => setEligibility(null))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setSubmitting(true);
        try {
            const r = await api.post('/r/testimonials', { quote });
            setSuccess(r.data.message || 'Testimonial trimis.');
            setQuote('');
            load();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    const existing = eligibility?.testimonial;

    return (
        <div className="panel" style={{ maxWidth: 640, marginBottom: '2rem' }} data-testid="restaurant-testimonial-settings">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '0.75rem' }}>
                <MessageSquareQuote size={18} color="var(--amber)" />
                <div>
                    <div className="kicker">Testimonial</div>
                    <h2 className="headline" style={{ fontSize: '1.35rem', margin: 0 }}>Spune-ne experiența ta</h2>
                </div>
            </div>
            <p style={{ color: 'var(--text-muted)', marginTop: 0, marginBottom: '1rem', lineHeight: 1.5, fontSize: '0.92rem' }}>
                După {eligibility?.min_days ?? 30} zile de la înregistrare poți lăsa un testimonial care poate apărea pe homepage-ul platformei.
            </p>

            {loading ? (
                <p style={{ color: 'var(--text-muted)', margin: 0 }}>Se încarcă...</p>
            ) : existing ? (
                <div className="testimonial-existing" data-testid="restaurant-testimonial-existing">
                    <div style={{ display: 'flex', gap: 2, marginBottom: '0.5rem' }}>
                        {[1, 2, 3, 4, 5].map((s) => (
                            <Star key={s} size={13} fill="var(--amber)" color="var(--amber)" />
                        ))}
                    </div>
                    <p style={{ margin: '0 0 0.75rem', lineHeight: 1.55, color: 'var(--text)' }}>"{existing.quote}"</p>
                    <div style={{ fontSize: '0.84rem', color: 'var(--text-muted)' }}>
                        Status: <span className={`badge badge-${existing.status === 'approved' ? 'teal' : existing.status === 'pending' ? 'cyan' : 'magenta'}`}>
                            {STATUS_LABEL[existing.status] || existing.status}
                        </span>
                        {' · '}{existing.created_at?.slice(0, 10)}
                    </div>
                    {existing.status === 'rejected' && eligibility?.can_post && (
                        <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem', marginTop: '0.75rem', marginBottom: 0 }}>
                            Poți trimite un testimonial nou mai jos.
                        </p>
                    )}
                </div>
            ) : null}

            {!loading && eligibility?.can_post && (
                <form onSubmit={submit} style={{ marginTop: existing ? '1rem' : 0 }}>
                    {error && (
                        <div style={{
                            padding: '0.75rem 1rem', marginBottom: '0.85rem', borderRadius: 8,
                            background: 'rgba(255,46,146,0.1)', border: '1px solid rgba(255,46,146,0.35)',
                            color: 'var(--neon-magenta)', fontSize: '0.88rem',
                        }} data-testid="testimonial-error">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div style={{
                            padding: '0.75rem 1rem', marginBottom: '0.85rem', borderRadius: 8,
                            background: 'rgba(20,132,143,0.12)', border: '1px solid rgba(20,132,143,0.35)',
                            color: 'var(--neon-teal)', fontSize: '0.88rem',
                        }} data-testid="testimonial-success">
                            {success}
                        </div>
                    )}
                    <label className="label" htmlFor="restaurant-testimonial-quote">Testimonialul tău</label>
                    <textarea
                        id="restaurant-testimonial-quote"
                        className="input"
                        rows={4}
                        minLength={10}
                        maxLength={500}
                        required
                        value={quote}
                        onChange={(e) => setQuote(e.target.value)}
                        placeholder="Spune-ne cum te-a ajutat platforma în restaurantul tău..."
                        data-testid="restaurant-testimonial-quote"
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.75rem', gap: '1rem', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.82rem', color: 'var(--text-faint)' }}>{quote.length}/500</span>
                        <button
                            type="submit"
                            className="btn btn-primary btn-sm"
                            disabled={submitting || quote.trim().length < 10}
                            data-testid="restaurant-testimonial-submit"
                        >
                            {submitting ? <span className="spinner" /> : <>Trimite testimonial <Send size={14} /></>}
                        </button>
                    </div>
                </form>
            )}

            {!loading && !eligibility?.can_post && !existing && (
                <div style={{
                    padding: '0.85rem 1rem', borderRadius: 8,
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    color: 'var(--text-muted)', fontSize: '0.9rem',
                }} data-testid="testimonial-locked">
                    {eligibility?.reason || 'Nu poți posta un testimonial încă.'}
                    {eligibility?.days_remaining > 0 && (
                        <div style={{ marginTop: 4, fontWeight: 600, color: 'var(--amber)' }}>
                            Mai ai {eligibility.days_remaining} zile.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
